import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCircle, AlertCircle, Sparkles, User } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";

export default function IsiSosiometriPage() {
  const { periodeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [unsurList, setUnsurList] = useState([]);
  const [ratings, setRatings] = useState({}); // {target_id: {unsur_no: 4|5}}
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [autoFillEnabled, setAutoFillEnabled] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/siswa/periode/${periodeId}`),
      api.get("/constants"),
    ]).then(([p, c]) => {
      setData(p.data);
      setUnsurList(c.data.unsur_penilaian);
      setAutoFillEnabled(!!c.data.auto_fill_enabled);
      if (p.data.submission?.ratings) {
        setRatings(p.data.submission.ratings);
      }
      if (p.data.sudah_mengisi) {
        setSubmitted(true);
        setSubmittedAt(p.data.submission?.submitted_at);
      }
    }).catch((e) => toast.error(apiErrorMsg(e)));
  }, [periodeId]);

  const setNilai = (targetId, unsurNo, value) => {
    setRatings((r) => ({
      ...r,
      [targetId]: { ...(r[targetId] || {}), [String(unsurNo)]: value }
    }));
  };

  const isiOtomatis = () => {
    if (!data) return;
    const next = {};
    data.targets.forEach((t) => {
      next[t.id] = {};
      for (let u = 1; u <= 23; u++) {
        next[t.id][String(u)] = Math.random() > 0.4 ? 5 : 4;
      }
    });
    setRatings(next);
    toast.success("Diisi otomatis (demo). Edit sesuai penilaian Anda.");
  };

  const totalFilled = useMemo(() => {
    if (!data) return 0;
    let n = 0;
    data.targets.forEach((t) => {
      for (let u = 1; u <= 23; u++) {
        if ([4, 5].includes(ratings[t.id]?.[String(u)])) n++;
      }
    });
    return n;
  }, [ratings, data]);

  const totalNeeded = data ? data.targets.length * 23 : 0;

  const submit = async () => {
    if (totalFilled < totalNeeded) {
      return toast.error(`Lengkapi semua nilai (${totalFilled}/${totalNeeded} terisi)`);
    }
    try {
      const res = await api.post(`/siswa/periode/${periodeId}/submit`, { ratings });
      setSubmitted(true);
      setSubmittedAt(res.data.submitted_at);
      toast.success("Berhasil dikirim!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  if (!data || unsurList.length === 0) return <div className="p-4 md:p-8 text-slate-500">Memuat...</div>;

  // Group by kategori
  const groups = [];
  let cur = null;
  unsurList.forEach((u) => {
    if (!cur || cur.kategori !== u.kategori) {
      cur = { kategori: u.kategori, label: u.kategori_label, items: [] };
      groups.push(cur);
    }
    cur.items.push(u);
  });

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-12">
      <Link to="/siswa" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 mb-4"><ArrowLeft className="w-4 h-4" /> Kembali ke daftar</Link>

      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Pengisian Sosiometri</div>
        <h1 className="font-heading text-3xl font-bold text-navy-900">Penilaian {data.jenis === "mingguan" ? "Mingguan" : "Bulanan"} ke-{data.nomor}</h1>
        <p className="text-slate-500 mt-1 text-sm">{data.bulan} {data.tahun} • Nilai 3 rekan di bawah dengan skala <span className="font-bold text-emerald-700">4</span> atau <span className="font-bold text-blue-700">5</span></p>
      </div>

      {submitted && (
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-sm p-5 mb-6 flex items-start gap-4" data-testid="bukti-pengisian">
          <CheckCircle className="w-8 h-8 text-emerald-600 flex-shrink-0" />
          <div>
            <h3 className="font-heading font-bold text-emerald-900 text-lg mb-1">Bukti Pengisian Tersimpan</h3>
            <p className="text-emerald-800 text-sm">Anda telah mengirim penilaian sosiometri pada <strong>{new Date(submittedAt).toLocaleString("id-ID", { dateStyle: "full", timeStyle: "short" })}</strong>.</p>
            <p className="text-emerald-700 text-xs mt-1">Anda masih bisa mengubah penilaian selama periode masih aktif. Klik "Kirim Ulang" untuk memperbarui.</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Status Pengisian</div>
            <div className="text-2xl font-bold font-mono text-navy-900">{totalFilled}/{totalNeeded}</div>
          </div>
          <div className="flex-1 max-w-md">
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-brass-500 transition-all" style={{ width: `${(totalFilled / totalNeeded) * 100}%` }} />
            </div>
          </div>
          <div className="flex gap-2">
            {autoFillEnabled && (
              <button onClick={isiOtomatis} data-testid="btn-isi-otomatis" className="px-3 py-2 border border-slate-300 hover:border-brass-500 rounded-sm text-sm flex items-center gap-2"><Sparkles className="w-4 h-4" /> Isi Otomatis</button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <strong>Petunjuk:</strong> Isi nilai <span className="font-mono font-bold">4</span> atau <span className="font-mono font-bold">5</span> untuk setiap unsur penilaian, untuk masing-masing 3 rekan Anda di bawah. Total {totalNeeded} kotak harus terisi.
        </div>
      </div>

      <div className="overflow-x-auto bg-white border border-slate-200 rounded-sm mb-4">
        <table className="w-full text-sm" data-testid="sosio-form-table">
          <thead className="sticky top-0 z-10">
            <tr className="bg-navy-900 text-white">
              <th className="p-3 border border-navy-800 text-left min-w-[160px] md:min-w-[280px] sticky left-0 z-20 bg-navy-900" rowSpan="2">UNSUR PENILAIAN</th>
              {data.targets.map((t, i) => (
                <th key={t.id} className="p-3 border border-navy-800 text-center min-w-[120px]">
                  <div className="font-bold">{t.nama}</div>
                  <div className="text-[10px] text-brass-200 font-normal mt-0.5">{t.nosis}</div>
                  <div className="text-[10px] text-white/60 mt-0.5 font-normal">{["A", "B", "C"][i]}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.kategori}>
                <tr className="bg-brass-100">
                  <td className="p-2 border border-brass-300 font-bold text-navy-900 text-xs uppercase tracking-wide" colSpan={1 + data.targets.length}>
                    {g.kategori}. {g.label}
                  </td>
                </tr>
                {g.items.map((u) => (
                  <tr key={u.no} className="border-t border-slate-100">
                    <td className="p-2 border border-slate-200 sticky left-0 z-[5] bg-white text-xs md:text-sm">
                      <span className="font-mono text-slate-500 mr-2">{u.no}.</span>{u.nama}
                    </td>
                    {data.targets.map((t) => {
                      const v = ratings[t.id]?.[String(u.no)];
                      return (
                        <td key={t.id} className="p-1 border border-slate-200 text-center">
                          <div className="flex gap-1 justify-center">
                            <button type="button" onClick={() => setNilai(t.id, u.no, 4)} data-testid={`sosio-${t.id}-${u.no}-4`} className={`w-9 h-8 rounded-sm font-mono font-bold text-sm border transition-all ${v === 4 ? "bg-emerald-500 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-300 hover:border-emerald-400 hover:text-emerald-600"}`}>4</button>
                            <button type="button" onClick={() => setNilai(t.id, u.no, 5)} data-testid={`sosio-${t.id}-${u.no}-5`} className={`w-9 h-8 rounded-sm font-mono font-bold text-sm border transition-all ${v === 5 ? "bg-blue-600 text-white border-blue-700" : "bg-white text-slate-500 border-slate-300 hover:border-blue-400 hover:text-blue-600"}`}>5</button>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex justify-end gap-3 sticky bottom-4">
        <button onClick={submit} data-testid="sosio-submit-btn" className="bg-brass-500 hover:bg-brass-600 text-white px-6 py-3 rounded-sm font-semibold text-sm flex items-center gap-2 shadow-lg">
          <Save className="w-4 h-4" /> {submitted ? "Kirim Ulang" : "Kirim Penilaian"} ({totalFilled}/{totalNeeded})
        </button>
      </div>
    </div>
  );
}
